/**
 * composer-v2/lib/emojiShortcodeTransformer — pure helpers + a Lexical
 * `TextMatchTransformer` factory for `:shortcode:` → unicode-glyph
 * autoreplace. KAP-307.
 *
 * Pattern mirrors `composer-v2/lib/mentionTrigger.js`: regex + lookup
 * isolated from any React/Lexical state, so it's cheap to unit-test
 * and re-usable from V1 if we ever revive it.
 *
 * Coverage: full `@emoji-mart/data` (~1800 emoji, Slack-equivalent),
 * including alias shortcodes (`+1`, `-1`) resolved via `data.aliases`.
 * Workspace-custom emoji is explicitly out of scope and lands in a
 * later epic.
 */

import data from '@emoji-mart/data';
import { $createTextNode } from 'lexical';
import { $getNearestNodeOfType } from '@lexical/utils';
import { CodeNode } from '@lexical/code';

const SHORTCODE_PATTERN_INNER = '[a-z0-9_+-]+';

/**
 * Resolve a shortcode (e.g. `thumbsup`, `+1`, `rocket`) to its unicode
 * glyph. Returns `null` for unknown shortcodes so callers can leave
 * the typed text untouched.
 *
 *   lookupEmojiGlyph('thumbsup') → '👍'
 *   lookupEmojiGlyph('+1')       → '👍'   (alias)
 *   lookupEmojiGlyph('Rocket')   → '🚀'   (case-insensitive)
 *   lookupEmojiGlyph('not_real') → null
 */
export function lookupEmojiGlyph(shortcode) {
  if (typeof shortcode !== 'string' || shortcode.length === 0) return null;
  const key = shortcode.toLowerCase();
  // `@emoji-mart/data.aliases` maps colloquial names (`+1`, `thumbsup`)
  // to canonical ids when they differ; fall back to the key directly
  // when the alias map has nothing.
  const resolved = (data.aliases && data.aliases[key]) || key;
  const entry = data.emojis && data.emojis[resolved];
  const skin = entry && entry.skins && entry.skins[0];
  return (skin && skin.native) || null;
}

/**
 * Build a Lexical markdown `TextMatchTransformer` for `:shortcode:`
 * autoreplace. Append to `TRANSFORMERS` at the
 * `<MarkdownShortcutPlugin>` mount site:
 *
 *   <MarkdownShortcutPlugin
 *     transformers={[...TRANSFORMERS, createEmojiShortcodeTransformer()]}
 *   />
 *
 * Fires when the user types the closing `:` (mirrors how the built-in
 * `[text](url)` autolink fires on the closing `)`).
 *
 * Guarded against running inside a `CodeNode` so that `:hover` inside
 * a fenced code block stays literal — matches the
 * `EnterToSendPlugin` deferral pattern in `MessageComposerV2.js`.
 */
export function createEmojiShortcodeTransformer() {
  const regExp = new RegExp(`:(${SHORTCODE_PATTERN_INNER}):$`, 'i');
  const importRegExp = new RegExp(`:(${SHORTCODE_PATTERN_INNER}):`, 'i');
  return {
    dependencies: [],
    // Glyphs pass through the markdown serializer as plain UTF-8 — no
    // rewrite back to `:shortcode:`. Slack does the same; reactions in
    // chat read the glyph directly.
    export: () => null,
    importRegExp,
    regExp,
    replace: (textNode, match) => {
      const shortcode = match && match[1];
      const glyph = lookupEmojiGlyph(shortcode);
      if (!glyph) return;
      if ($getNearestNodeOfType(textNode, CodeNode)) return;
      textNode.replace($createTextNode(glyph));
    },
    trigger: ':',
    type: 'text-match'
  };
}
